import React, { useState, useRef } from 'react';
import { Input } from './ui/input';
import { User } from 'lucide-react';

/**
 * TrainerAutocomplete — controlled input with dropdown from a passed trainers array.
 *
 * Props:
 *  value        — controlled string value (trainer name)
 *  trainers     — array of { id, name, email } from the roster
 *  onChange     — called on every keystroke with current string
 *  onCommit     — called when user picks or blurs (finalises value)
 *  placeholder
 *  testId
 *  autoFocus
 */
export function TrainerAutocomplete({
  value = '',
  trainers = [],
  onChange,
  onCommit,
  placeholder = 'Type trainer name…',
  testId = '',
  autoFocus = false,
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const filtered = query.trim().length >= 1
    ? trainers.filter(t => (t.name || '').toLowerCase().includes(query.toLowerCase()))
    : trainers.slice(0, 12); // show first 12 when empty

  const handleInputChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    if (onChange) onChange(v);
  };

  const handleSelect = (trainer) => {
    setQuery(trainer.name);
    setOpen(false);
    if (onChange) onChange(trainer.name);
    if (onCommit) onCommit(trainer.name);
  };

  const handleBlur = () => {
    // Small delay so onMouseDown on dropdown items fires first
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setOpen(false);
        if (onCommit) onCommit(query);
      }
    }, 120);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); if (onCommit) onCommit(query); }
    if (e.key === 'Enter') { setOpen(false); if (onCommit) onCommit(query); }
  };

  return (
    <div ref={containerRef} className="relative" data-testid={`trainer-ac-wrap-${testId}`}>
      <Input
        value={query}
        onChange={handleInputChange}
        onFocus={() => { setQuery(value); setOpen(true); }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid={testId}
        className="h-8 text-sm"
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div
          className="absolute z-50 top-full left-0 right-0 mt-0.5 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto"
          data-testid={`trainer-dropdown-${testId}`}
        >
          {filtered.map((t) => (
            <button
              key={t.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-muted/60 transition-colors flex items-center gap-2 border-b border-border/40 last:border-0"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(t); }}
              data-testid={`trainer-option-${t.id}`}
            >
              <div className="w-6 h-6 rounded-full bg-lime/20 flex items-center justify-center flex-shrink-0">
                <User className="w-3 h-3 text-lime-700" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{t.name}</p>
                {t.email && <p className="text-xs text-muted-foreground truncate">{t.email}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
